
import * as stylex from '@stylexjs/stylex';

const MEDIA_768 = '@media (max-width: 768px)';
const MEDIA_480 = '@media (max-width: 480px)';

export const styles = stylex.create({
    timelinePage: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: 'var(--spacing-lg)',
        color: 'var(--text-primary)',
        minHeight: '100vh',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transition: 'color var(--transition-fast)',
        ':hover': {
            color: 'var(--primary)',
        },
    },
    pageHeader: {
        marginBottom: 'var(--spacing-3xl)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
    headerContent: {
        maxWidth: 800,
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: 800,
        margin: '0 0 var(--spacing-md) 0',
        color: 'var(--text-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--spacing-md)',
        [MEDIA_768]: {
            fontSize: '2.5rem',
        },
        [MEDIA_480]: {
            fontSize: '2rem',
            flexDirection: 'column',
        },
    },
    titleIcon: {
        color: 'var(--primary)',
    },
    pageSubtitle: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        maxWidth: 600,
        margin: '0 auto',
        [MEDIA_768]: {
            fontSize: '1rem',
        },
    },

    /* Timeline Steps Section */
    timelineSection: {
        marginBottom: 'var(--spacing-4xl)',
        position: 'relative',
    },
    stepsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-3xl)',
        position: 'relative',
        '::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 24, // Align with step number center
            width: 2,
            backgroundColor: 'var(--border)',
            zIndex: 0,
            [MEDIA_768]: {
                left: 16,
            },
        },
    },
    stepRow: {
        display: 'grid',
        gridTemplateColumns: '50px 1fr 1fr',
        gap: 'var(--spacing-xl)',
        position: 'relative',
        zIndex: 1,
        [MEDIA_768]: {
            gridTemplateColumns: '40px 1fr',
            gap: 'var(--spacing-lg)',
        },
        [MEDIA_480]: {
            gridTemplateColumns: '40px 1fr',
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: 50,
        },
    },
    stepNumber: {
        width: 50,
        height: 50,
        borderRadius: '50%',
        backgroundColor: 'var(--bg-card)',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'var(--primary)',
        color: 'var(--primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.25rem',
        fontWeight: 'bold',
        position: 'relative',
        zIndex: 2,
        boxShadow: `0 0 0 8px var(--bg)`, // Mask the timeline line
        [MEDIA_768]: {
            width: 40,
            height: 40,
            fontSize: '1rem',
        },
        [MEDIA_480]: {
            position: 'absolute',
            left: 0,
            top: 0,
        },
    },
    noNumberLine: {
        // Special handling if needed for the last item not to have a line extending, 
        // but the line is global container based, so this might just be a hook for specific styles 
    },
    stepContent: {
        paddingTop: 'var(--spacing-xs)',
        [MEDIA_768]: {
            gridColumn: '2',
        },
        [MEDIA_480]: {
            width: '100%',
        },
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
        marginBottom: 'var(--spacing-md)',
    },
    stepIcon: {
        color: 'var(--accent)',
        backgroundColor: 'var(--accent-bg-subtle)',
        padding: 8,
        borderRadius: 'var(--radius-md)',
    },
    stepTitle: {
        fontSize: '1.5rem',
        fontWeight: 700,
        margin: 0,
        color: 'var(--text-primary)',
        [MEDIA_768]: {
            fontSize: '1.25rem',
        },
    },
    stepDescription: {
        fontSize: '1rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-lg)',
    },
    stepFeatures: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-xs)',
    },
    stepFeature: {
        fontSize: '0.9rem',
        color: 'var(--text-secondary)',
        display: 'flex',
        gap: 'var(--spacing-sm)',
    },

    /* Step Images */
    stepImageGallery: {
        display: 'flex',
        gap: 'var(--spacing-md)',
        [MEDIA_768]: {
            gridColumn: '2',
            marginTop: 'var(--spacing-md)',
        },
        [MEDIA_480]: {
            width: '100%',
            overflowX: 'auto',
            paddingBottom: 'var(--spacing-sm)', // For scrollbar
        },
    },
    gallerySingle: {
        // Just one image
    },
    galleryMulti: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 'var(--spacing-sm)',
    },
    stepImageContainer: {
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-card)',
        boxShadow: 'var(--shadow-md)',
        transition: 'transform var(--transition-fast)',
        ':hover': {
            transform: 'scale(1.02)',
        },
    },
    stepImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },

    /* Additional Features */
    additionalFeaturesSection: {
        marginBottom: 'var(--spacing-4xl)',
    },
    featuresGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 'var(--spacing-lg)',
    },
    featureCard: {
        display: 'flex',
        flexDirection: 'column', // Changed to column for better alignment with content
        alignItems: 'flex-start',
        padding: 'var(--spacing-xl)',
        backgroundColor: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        borderWidth: 1,
        marginTop: 'var(--spacing-2xl)',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        textDecoration: 'none',
        transition: 'all var(--transition-normal)',
        position: 'relative',
        ':hover': {
            transform: 'translateY(-4px)',
            borderColor: 'var(--primary)',
            boxShadow: 'var(--shadow-lg)',
        },
    },
    featureIcon: {
        width: 48,
        height: 48,
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--primary-bg-subtle)',
        color: 'var(--primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 'var(--spacing-md)',
    },
    featureContent: {
        flex: 1,
    },
    featureTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        margin: '0 0 var(--spacing-xs) 0',
        color: 'var(--text-primary)',
    },
    featureDescription: {
        fontSize: '0.9rem',
        color: 'var(--text-secondary)',
        margin: 0,
        lineHeight: 1.5,
    },
    featureArrow: {
        position: 'absolute',
        top: 'var(--spacing-md)',
        right: 'var(--spacing-md)',
        color: 'var(--text-tertiary)',
        transition: 'all var(--transition-fast)',
        opacity: 0,
    },
    featureArrowActive: {
        opacity: 1,
        transform: 'translateX(4px)',
        color: 'var(--primary)',
    },

    /* CTA Section */
    ctaSection: {
        textAlign: 'center',
        padding: 'var(--spacing-2xl)',
        backgroundColor: 'var(--bg-secondary)', // Use secondary bg instead of distinct gradient for now
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-2xl)',
        marginTop: 'var(--spacing-2xl)',
        position: 'relative',
        overflow: 'hidden',
    },
    ctaContent: {
        position: 'relative',
        zIndex: 2,
        maxWidth: 600,
        margin: '0 auto',
    },
    ctaTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        marginBottom: 'var(--spacing-md)',
        color: 'var(--text-primary)',
    },
    ctaDescription: {
        fontSize: '1.1rem',
        color: 'var(--text-secondary)',
        marginBottom: 'var(--spacing-xl)',
        lineHeight: 1.6,
    },
    ctaButtons: {
        display: 'flex',
        justifyContent: 'center',
        gap: 'var(--spacing-md)',
    },
    primaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        backgroundColor: 'var(--primary)',
        color: 'var(--text-on-accent)',
        padding: '0.75rem 1.5rem', // Direct padding as token might not be perfect for button
        borderRadius: 'var(--radius-full)',
        fontWeight: 600,
        textDecoration: 'none',
        transition: 'all var(--transition-fast)',
        ':hover': {
            backgroundColor: 'var(--primary-hover)',
            transform: 'scale(1.05)',
        },
    },
});
